import { useCallback } from 'react';

import { useDeviceType } from '@cian/ui-kit';

import { BlueBanner } from 'shared/components/BlueBanner';
import { GuideStartBlock } from 'shared/components/GuideStartBlock';
import { NBSP } from 'shared/constants/symbols';
import { FaqContainer } from 'shared/containers/Faq';
import { SocialsContainer } from 'shared/containers/Socials';
import { StepsContainer } from 'shared/containers/Steps';
import { SwitcherContainer } from 'shared/containers/Switcher';
import { useAppDispatch, useSobFilters } from 'shared/hooks';
import { setDealType, setOfferType } from 'shared/slices';
import type { TStep } from 'shared/types/carousel';
import type { TFaqItem } from 'shared/types/faq';
import { EDealType, EOfferType } from 'shared/types/features';
import type { TSocials } from 'shared/types/socials';
import { useApplicationContext } from 'shared/utils/applicationContext';
import { getSobPreFooterBanners } from 'shared/utils/banners';
import { filterStepsByFilters } from 'shared/utils/carousel';

export const SobContentContainer: React.FC = () => {
  const device = useDeviceType();
  const dispatch = useAppDispatch();
  const { config } = useApplicationContext();
  const { dealType, offerType } = useSobFilters();

  const title = `Как эффективно размещаться на${NBSP}Циан`;
  const description = `Мы расскажем, как сделать привлекательное объявление и${NBSP}найти реального покупателя`;
  const steps = config.get<TStep[]>('b2b-marketing-guide-frontend.sob.steps') ?? [];
  const filteredSteps = filterStepsByFilters(steps, { dealType, offerType });
  const faqItems = config.get<TFaqItem[] | null>('b2b-marketing-guide-frontend.sob.faq');
  const faq = faqItems?.length ? <FaqContainer items={faqItems} compactTop={filteredSteps.length % 2 === 0} /> : null;
  const socialsConfig = config.get<TSocials | null>('b2b-marketing-guide-frontend.sob.socials');
  const socials = socialsConfig ? <SocialsContainer socials={socialsConfig} /> : null;
  const { mainBanner, secondBanner } = getSobPreFooterBanners(dealType, offerType);

  const handleDealTypeChange = useCallback(
    (nextDealType: EDealType): void => {
      dispatch(setDealType(nextDealType));
    },
    [dispatch],
  );

  const handleOfferTypeChange = useCallback(
    (nextOfferType: EOfferType): void => {
      dispatch(setOfferType(nextOfferType));
    },
    [dispatch],
  );

  return (
    <div>
      <GuideStartBlock
        title={title}
        description={description}
        device={device}
        pageType="sob"
        dealType={dealType}
        offerType={offerType}
      />
      <SwitcherContainer
        withDealType
        dealType={dealType}
        offerType={offerType}
        onActiveDealType={handleDealTypeChange}
        onActiveOfferType={handleOfferTypeChange}
      />
      <StepsContainer steps={filteredSteps} pageType="sob" dealType={dealType} offerType={offerType} />
      {faq}
      <BlueBanner mainBanner={mainBanner} secondBanner={secondBanner} page />
      {socials}
    </div>
  );
};
